/**
 * A hand-rolled glTF 2.0 binary (.glb) writer.
 *
 * No dependency at all, not even THREE: the input is `MeshPiece[]` (see
 * `types.ts`) and the output is an ArrayBuffer. That is what makes it liftable
 * into the SDK, and it is also why the matrix maths is spelled out here
 * instead of borrowed from `Matrix4`.
 *
 * Two structures, because the two consumers want opposite things:
 *   merged — world transforms baked into vertices, one primitive per material.
 *            Fewest draw calls; what you drop into a game as scenery.
 *   nodes  — a named node per piece with meshes de-duplicated. Editable in
 *            Blender/Godot; what you want if the build is a starting point.
 *
 * Output is Y-up metres, which Godot 4 and Blender both import natively.
 */
import { centerOffset, transformPoint, worldBox } from "./bbox.mjs";
import { hexToLinear } from "./color.mjs";
import {
  DEFAULT_EXPORT_OPTIONS,
  type MeshExportInfo,
  type MeshPiece,
  type MeshExportOptions,
} from "./types";

/** glTF `bufferView.target` for vertex attribute data. */
const ARRAY_BUFFER = 34962;
/** glTF `accessor.componentType` for 32-bit float. */
const FLOAT = 5126;
/** glTF `primitive.mode` for triangles. */
const TRIANGLES = 4;

export function buildGLB(
  pieces: MeshPiece[],
  options?: Partial<MeshExportOptions>
): { buffer: ArrayBuffer; info: MeshExportInfo } {
  const opts: MeshExportOptions = { ...DEFAULT_EXPORT_OPTIONS, ...(options || {}) };
  if (!pieces.length) throw new Error("Nothing to export");

  const gs = opts.scale;
  const off = opts.center
    ? centerOffset(worldBox(pieces.map((p) => ({ box: p.box, pos: p.pos, rotY: p.rotY, scale: p.scale }))))
    : [0, 0, 0];

  const json: any = {
    asset: { version: "2.0", generator: opts.generator },
    scene: 0,
    scenes: [{ name: opts.name, nodes: [] as number[] }],
    nodes: [] as any[],
    meshes: [] as any[],
    materials: [] as any[],
    accessors: [] as any[],
    bufferViews: [] as any[],
    buffers: [{ byteLength: 0 }],
  };

  const bin: Uint8Array[] = [];
  let binLen = 0;

  /**
   * Append a float array as a bufferView.
   *
   * The bytes are COPIED out of the source: a Float32Array built over a
   * subarray still points at the parent buffer, and writing the view offsets
   * without detaching would emit whichever slice the parent happened to hold.
   * Every view is then 4-byte aligned, which the spec requires.
   */
  function addBufferView(f32: Float32Array): number {
    const bytes = new Uint8Array(f32.buffer, f32.byteOffset, f32.byteLength);
    const copy = new Uint8Array(bytes);
    const idx = json.bufferViews.length;
    json.bufferViews.push({
      buffer: 0,
      byteOffset: binLen,
      byteLength: copy.byteLength,
      target: ARRAY_BUFFER,
    });
    bin.push(copy);
    binLen += copy.byteLength;
    const pad = (4 - (binLen % 4)) % 4;
    if (pad) {
      bin.push(new Uint8Array(pad));
      binLen += pad;
    }
    return idx;
  }

  /**
   * `withMinMax` is not optional for POSITION — the spec requires bounds on it,
   * and importers that build a bounding volume from them (Godot, three's own
   * loader) place the model wrong or cull it entirely without them.
   */
  function addAccessor(f32: Float32Array, type: "VEC2" | "VEC3" | "VEC4" | "SCALAR", withMinMax: boolean) {
    const comp = { SCALAR: 1, VEC2: 2, VEC3: 3, VEC4: 4 }[type];
    const acc: any = {
      bufferView: addBufferView(f32),
      byteOffset: 0,
      componentType: FLOAT,
      count: f32.length / comp,
      type,
    };
    if (withMinMax) {
      const mn = new Array(comp).fill(Infinity);
      const mx = new Array(comp).fill(-Infinity);
      for (let i = 0; i < f32.length; i += comp)
        for (let k = 0; k < comp; k++) {
          if (f32[i + k] < mn[k]) mn[k] = f32[i + k];
          if (f32[i + k] > mx[k]) mx[k] = f32[i + k];
        }
      acc.min = mn;
      acc.max = mx;
    }
    json.accessors.push(acc);
    return json.accessors.length - 1;
  }

  /* ---- materials ---- */

  /**
   * Colour → material. The first slot that claims a hex names it, so a build
   * imports with readable material names ("stone", "roof") rather than
   * color_0..color_13, and `metal` / `flame` / `water` get the PBR treatment
   * their look depends on.
   */
  const slotForHex = new Map<string, string>();
  for (const p of pieces)
    for (const g of p.groups) if (!slotForHex.has(g.color)) slotForHex.set(g.color, g.slot);

  const matIdx = new Map<string, number>();
  function materialFor(hex: string): number {
    const found = matIdx.get(hex);
    if (found !== undefined) return found;
    const slotKey = slotForHex.get(hex);
    const lin = hexToLinear(hex);
    const m: any = {
      name: slotKey || `color_${json.materials.length}`,
      pbrMetallicRoughness: {
        baseColorFactor: [lin[0], lin[1], lin[2], 1],
        metallicFactor: slotKey === "metal" ? 0.3 : 0,
        roughnessFactor: slotKey === "metal" ? 0.6 : 0.92,
      },
      doubleSided: false,
    };
    if (slotKey === "flame")
      m.emissiveFactor = [Math.min(1, lin[0]), Math.min(1, lin[1]), Math.min(1, lin[2])];
    if (slotKey === "water") {
      m.alphaMode = "BLEND";
      m.pbrMetallicRoughness.baseColorFactor[3] = 0.8;
    }
    json.materials.push(m);
    matIdx.set(hex, json.materials.length - 1);
    return json.materials.length - 1;
  }

  function whiteMat(): number {
    json.materials.push({
      name: "palette",
      pbrMetallicRoughness: {
        baseColorFactor: [1, 1, 1, 1],
        metallicFactor: 0,
        roughnessFactor: 0.92,
      },
    });
    return json.materials.length - 1;
  }

  let totalVerts = 0;
  let totalPrims = 0;

  if (opts.mode === "merged") {
    /* ===== MERGED: bake world transforms into the vertices ===== */
    const buckets = new Map<string, { pos: number[]; nrm: number[]; col: number[] }>();

    for (const p of pieces) {
      const cos = Math.cos(p.rotY);
      const sin = Math.sin(p.rotY);
      for (const gr of p.groups) {
        // vertexColors collapses everything into one bucket, since a single
        // white material then carries every colour in COLOR_0
        const key = opts.vertexColors ? "*" : gr.color;
        let b = buckets.get(key);
        if (!b) {
          b = { pos: [], nrm: [], col: [] };
          buckets.set(key, b);
        }
        const lin = opts.vertexColors ? hexToLinear(gr.color) : null;
        for (let i = gr.start; i < gr.start + gr.count; i++) {
          const v = transformPoint(
            [p.position[i * 3], p.position[i * 3 + 1], p.position[i * 3 + 2]],
            p.pos, p.rotY, p.scale
          );
          b.pos.push((v[0] + off[0]) * gs, (v[1] + off[1]) * gs, (v[2] + off[2]) * gs);
          // normals take the rotation only — uniform scale leaves them intact
          const nx = p.normal[i * 3], ny = p.normal[i * 3 + 1], nz = p.normal[i * 3 + 2];
          b.nrm.push(nx * cos + nz * sin, ny, -nx * sin + nz * cos);
          if (lin) b.col.push(lin[0], lin[1], lin[2]);
        }
      }
    }

    const prims: any[] = [];
    const sharedWhite = opts.vertexColors ? whiteMat() : null;
    buckets.forEach((b, key) => {
      const attributes: any = {
        POSITION: addAccessor(new Float32Array(b.pos), "VEC3", true),
        NORMAL: addAccessor(new Float32Array(b.nrm), "VEC3", false),
      };
      if (opts.vertexColors) attributes.COLOR_0 = addAccessor(new Float32Array(b.col), "VEC3", false);
      prims.push({
        attributes,
        mode: TRIANGLES,
        material: opts.vertexColors ? sharedWhite : materialFor(key),
      });
      totalVerts += b.pos.length / 3;
      totalPrims++;
    });

    json.meshes.push({ name: "structure", primitives: prims });
    json.nodes.push({ name: opts.name, mesh: 0 });
    json.scenes[0].nodes.push(0);
  } else {
    /* ===== NODES: a named node per piece, meshes de-duplicated ===== */
    const meshDedup = new Map<string, number>();
    const sharedWhite = opts.vertexColors ? whiteMat() : null;

    function meshFor(p: MeshPiece): number {
      const found = meshDedup.get(p.dedupKey);
      if (found !== undefined) return found;
      const prims: any[] = [];
      if (opts.vertexColors) {
        const col = new Float32Array(p.position.length);
        for (const gr of p.groups) {
          const lin = hexToLinear(gr.color);
          for (let i = gr.start; i < gr.start + gr.count; i++) {
            col[i * 3] = lin[0];
            col[i * 3 + 1] = lin[1];
            col[i * 3 + 2] = lin[2];
          }
        }
        prims.push({
          attributes: {
            POSITION: addAccessor(new Float32Array(p.position), "VEC3", true),
            NORMAL: addAccessor(new Float32Array(p.normal), "VEC3", false),
            COLOR_0: addAccessor(col, "VEC3", false),
          },
          mode: TRIANGLES,
          material: sharedWhite,
        });
        totalVerts += p.position.length / 3;
        totalPrims++;
      } else {
        for (const gr of p.groups) {
          prims.push({
            attributes: {
              POSITION: addAccessor(
                new Float32Array(p.position.subarray(gr.start * 3, (gr.start + gr.count) * 3)),
                "VEC3", true
              ),
              NORMAL: addAccessor(
                new Float32Array(p.normal.subarray(gr.start * 3, (gr.start + gr.count) * 3)),
                "VEC3", false
              ),
            },
            mode: TRIANGLES,
            material: materialFor(gr.color),
          });
          totalVerts += gr.count;
          totalPrims++;
        }
      }
      json.meshes.push({ name: p.kind, primitives: prims });
      meshDedup.set(p.dedupKey, json.meshes.length - 1);
      return json.meshes.length - 1;
    }

    const childIdx: number[] = [];
    for (const p of pieces) {
      const half = p.rotY / 2;
      json.nodes.push({
        name: `${p.kind}_${p.id}`,
        mesh: meshFor(p),
        translation: [p.pos[0], p.pos[1], p.pos[2]],
        // quaternion about +Y, spelled out rather than via THREE.Quaternion
        rotation: [0, Math.sin(half), 0, Math.cos(half)],
        scale: [p.scale, p.scale, p.scale],
      });
      childIdx.push(json.nodes.length - 1);
    }
    // the centring offset and the global scale ride on ONE parent node, so the
    // per-piece transforms stay exactly what the editor showed
    json.nodes.push({
      name: opts.name,
      children: childIdx,
      translation: [off[0] * gs, off[1] * gs, off[2] * gs],
      scale: [gs, gs, gs],
    });
    json.scenes[0].nodes.push(json.nodes.length - 1);
  }

  /* ---- pack the GLB container ---- */
  json.buffers[0].byteLength = binLen;
  const jsonBytes = new TextEncoder().encode(JSON.stringify(json));
  // the JSON chunk pads with SPACES, the BIN chunk with zeroes — a spec detail
  // strict validators do check
  const jsonPad = (4 - (jsonBytes.length % 4)) % 4;
  const total = 12 + 8 + jsonBytes.length + jsonPad + 8 + binLen;
  const out = new ArrayBuffer(total);
  const dv = new DataView(out);
  const u8 = new Uint8Array(out);
  let o = 0;
  dv.setUint32(o, 0x46546c67, true); o += 4; // 'glTF'
  dv.setUint32(o, 2, true); o += 4;
  dv.setUint32(o, total, true); o += 4;
  dv.setUint32(o, jsonBytes.length + jsonPad, true); o += 4;
  dv.setUint32(o, 0x4e4f534a, true); o += 4; // 'JSON'
  u8.set(jsonBytes, o); o += jsonBytes.length;
  for (let i = 0; i < jsonPad; i++) u8[o++] = 0x20;
  dv.setUint32(o, binLen, true); o += 4;
  dv.setUint32(o, 0x004e4942, true); o += 4; // 'BIN'
  let bo = o;
  bin.forEach((chunk) => {
    u8.set(chunk, bo);
    bo += chunk.byteLength;
  });

  return {
    buffer: out,
    info: {
      verts: totalVerts,
      prims: totalPrims,
      mats: json.materials.length,
      nodes: json.nodes.length,
      bytes: total,
    },
  };
}
